import React, { useEffect, useState } from "react";
import { Copy, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const ctl = "h-9 px-2 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";

const HubKey = () => {
  const [hub, setHub] = useState(null);
  const load = (regenerate = false) => api.get("/gatepass/hub-key", { params: { regenerate } }).then((r) => setHub(r.data)).catch((e) => console.error(e));
  useEffect(() => { load(); }, []);
  const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied"));
  if (!hub) return null;
  const url = `${process.env.REACT_APP_BACKEND_URL}${hub.endpoint}`;
  return (
    <div data-testid="gp-hub-key" className="space-y-1.5 text-xs">
      <div className="font-semibold text-sm">Auto Inventory Hub connection</div>
      <p className="text-muted-foreground">Auto Inventory Hub sends the scanned QR to this address with this key to mark the vehicle out.</p>
      <div className="flex items-center gap-2"><span className="text-muted-foreground w-14">Address</span><code className="num break-all flex-1">{url}</code><button onClick={() => copy(url)} className="text-muted-foreground hover:text-primary"><Copy size={13} /></button></div>
      <div className="flex items-center gap-2"><span className="text-muted-foreground w-14">Key</span><code data-testid="gp-hub-key-value" className="num break-all flex-1">{hub.key}</code><button data-testid="gp-hub-key-copy" onClick={() => copy(hub.key)} className="text-muted-foreground hover:text-primary"><Copy size={13} /></button></div>
      <button data-testid="gp-hub-key-regen" onClick={() => window.confirm("Make a new key? Auto Inventory Hub must be updated with it.") && load(true)} className="h-8 px-3 rounded-md border border-input text-xs flex items-center gap-1.5 hover:bg-muted transition-colors"><RefreshCw size={12} /> New key</button>
    </div>
  );
};

const Vehicles = () => {
  const [list, setList] = useState([]);
  const [n, setN] = useState({ number: "", name: "" });
  useEffect(() => { api.get("/gatepass/meta").then((r) => setList(r.data.vehicles || [])).catch((e) => console.error(e)); }, []);
  const save = async (vs) => {
    try { setList((await api.put("/gatepass/vehicles", { vehicles: vs })).data.vehicles); toast.success("Company vehicles saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2">
      <div className="font-semibold text-sm">Company bikes / service vans</div>
      {list.map((v) => (
        <div key={v.number} data-testid={`gp-veh-${v.number}`} className="flex items-center justify-between text-sm border-b border-border py-1">
          <span className="num">{v.number}{v.name ? <span className="text-muted-foreground"> · {v.name}</span> : null}</span>
          <button data-testid={`gp-veh-del-${v.number}`} onClick={() => save(list.filter((x) => x.number !== v.number))} className="text-muted-foreground hover:text-destructive"><Trash2 size={13} /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <input data-testid="gp-veh-number" className={`${ctl} w-28 sm:w-36 num`} placeholder="Vehicle no." value={n.number} onChange={(e) => setN({ ...n, number: e.target.value.toUpperCase() })} />
        <input data-testid="gp-veh-name" className={`${ctl} flex-1 min-w-0`} placeholder="e.g. Service van 1" value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} />
        <button data-testid="gp-veh-add" disabled={!n.number.trim()} onClick={() => { save([...list, n]); setN({ number: "", name: "" }); }} className="h-9 w-9 rounded-md border border-input flex items-center justify-center hover:bg-muted disabled:opacity-40"><Plus size={14} /></button>
      </div>
    </div>
  );
};

const AlertPhones = () => {
  const [phones, setPhones] = useState([]);
  const [n, setN] = useState("");
  useEffect(() => { api.get("/gatepass/meta").then((r) => setPhones(r.data.alert_phones || [])).catch((e) => console.error(e)); }, []);
  const save = async (list) => {
    try { setPhones((await api.put("/gatepass/alert-phones", { phones: list })).data.phones); toast.success("Alert numbers saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendNow = async () => {
    try {
      const { data } = await api.post("/gatepass/alerts/send-now");
      toast.success(data.late ? `${data.late} late vehicle(s) · sent to ${data.sent} number(s)` : "No vehicle is inside for more than 2 days");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="gp-alert-phones" className="space-y-2 lg:col-span-2 border-t border-border pt-4">
      <div className="font-semibold text-sm">Late vehicle WhatsApp alerts</div>
      <p className="text-xs text-muted-foreground">Every day at 10 am, these numbers get a WhatsApp list of vehicles still inside more than 2 days after their gate pass was made.</p>
      {phones.map((p) => (
        <div key={p} data-testid={`gp-alert-${p}`} className="flex items-center justify-between text-sm border-b border-border py-1 max-w-md">
          <span className="num">{p}</span>
          <button data-testid={`gp-alert-del-${p}`} onClick={() => save(phones.filter((x) => x !== p))} className="text-muted-foreground hover:text-destructive"><Trash2 size={13} /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <input data-testid="gp-alert-input" className={`${ctl} w-48 num`} placeholder="Admin WhatsApp no." value={n} onChange={(e) => setN(e.target.value)} />
        <button data-testid="gp-alert-add" disabled={!n.trim()} onClick={() => { save([...phones, n.trim()]); setN(""); }} className="h-9 w-9 rounded-md border border-input flex items-center justify-center hover:bg-muted disabled:opacity-40"><Plus size={14} /></button>
        {phones.length > 0 && <button data-testid="gp-alert-send-now" onClick={sendNow} className="h-9 px-3 rounded-md border border-input text-xs hover:bg-muted transition-colors">Send alert now</button>}
      </div>
    </div>
  );
};

export const GatePassSettings = () => (
  <section data-testid="gp-settings" className="grid grid-cols-1 lg:grid-cols-2 gap-6 rounded-xl border border-border bg-card p-5 mb-6">
    <Vehicles />
    <HubKey />
    <AlertPhones />
  </section>
);
